import { TOOLS, getInitialCropRect } from '../config';
import { runsToText } from '../elements/text/textRuns';
import { createEmptyDocument, createInitialState, getElementInsertIndex, getTextDisplayName } from './documentSchema';

const mergeElementChanges = (element, payloadChanges) => {
  const changes = { ...payloadChanges };
  if (changes.filters) {
    changes.filters = { ...element.filters, ...changes.filters };
  }
  if (changes.shadow) {
    changes.shadow = { ...element.shadow, ...changes.shadow };
  }
  if (changes.background) {
    changes.background = { ...element.background, ...changes.background };
  }
  if (changes.outline) {
    changes.outline = { ...element.outline, ...changes.outline };
  }
  if (element.type === 'text' && changes.runs) {
    changes.text = runsToText(changes.runs);
    if (changes.name == null) {
      changes.name = getTextDisplayName({ text: changes.text });
    }
  }
  return { ...element, ...changes };
};

const withoutIds = (state, ids) => {
  const removed = new Set(ids);
  const selectedIds = state.selectedIds.filter((id) => !removed.has(id));
  const selectedId = removed.has(state.selectedId) ? selectedIds[selectedIds.length - 1] ?? null : state.selectedId;
  return {
    ...state,
    elements: state.elements.filter((item) => !removed.has(item.id)),
    selectedId,
    selectedIds,
    hasDocument: true,
  };
};

const leaveCrop = (state) => ({
  tool: state.tool === TOOLS.CROP ? TOOLS.SELECT : state.tool,
  cropRect: state.tool === TOOLS.CROP ? null : state.cropRect,
});

export const mediaStudioReducer = (state, action) => {
  switch (action.type) {
    case 'SET_DOCUMENT_FROM_IMAGE': {
      const { element, width, height, viewport } = action.payload;
      return {
        ...createEmptyDocument({ width, height, backgroundColor: '#ffffff' }),
        background: { type: 'color', color: '#ffffff' },
        elements: [element],
        selectedId: element.id,
        selectedIds: [element.id],
        viewport: viewport || { zoom: 1, x: 0, y: 0 },
        hasDocument: true,
      };
    }
    case 'START_FORMAT_CROP':
      return {
        ...state,
        tool: TOOLS.CROP,
        cropRatio: action.payload.ratio,
        activeFormatId: action.payload.formatId,
        pendingOutputSize: action.payload.outputSize || null,
        cropRect: getInitialCropRect(state.width, state.height, action.payload.ratio),
      };
    case 'ADD_ELEMENT': {
      const elements = [...state.elements];
      elements.splice(getElementInsertIndex(elements, action.payload), 0, action.payload);
      return {
        ...state,
        elements,
        selectedId: action.payload.id,
        selectedIds: [action.payload.id],
        tool: TOOLS.SELECT,
        hasDocument: true,
      };
    }
    case 'INSERT_ELEMENTS': {
      const { elements, ids } = action.payload;
      return {
        ...state,
        elements,
        selectedId: ids[ids.length - 1] ?? null,
        selectedIds: ids,
        tool: TOOLS.SELECT,
        hasDocument: true,
      };
    }
    case 'UPDATE_ELEMENT':
      return {
        ...state,
        elements: state.elements.map((element) =>
          element.id === action.payload.id ? mergeElementChanges(element, action.payload.changes) : element,
        ),
      };
    case 'UPDATE_ELEMENTS': {
      const updates = new Map(action.payload.map(({ id, changes }) => [id, changes]));
      return {
        ...state,
        elements: state.elements.map((element) =>
          updates.has(element.id) ? mergeElementChanges(element, updates.get(element.id)) : element,
        ),
      };
    }
    case 'SELECT_ELEMENT':
      return {
        ...state,
        selectedId: action.payload,
        selectedIds: action.payload ? [action.payload] : [],
        ...leaveCrop(state),
      };
    case 'SELECT_ELEMENTS': {
      const { ids, primaryId } = action.payload;
      return {
        ...state,
        selectedId: ids.includes(primaryId) ? primaryId : ids[ids.length - 1] ?? null,
        selectedIds: ids,
        ...leaveCrop(state),
      };
    }
    case 'REORDER_ELEMENTS':
      return {
        ...state,
        elements: action.payload,
      };
    case 'DELETE_ELEMENT':
      return withoutIds(state, [action.payload]);
    case 'DELETE_ELEMENTS':
      return withoutIds(state, action.payload);
    case 'TOGGLE_VISIBILITY':
      return {
        ...state,
        elements: state.elements.map((element) =>
          element.id === action.payload ? { ...element, visible: !element.visible } : element,
        ),
      };
    case 'SET_TOOL': {
      const tool = action.payload;
      const cropRect = tool === TOOLS.CROP ? getInitialCropRect(state.width, state.height, state.cropRatio) : null;
      return {
        ...state,
        tool,
        cropRect,
        pendingOutputSize: tool === TOOLS.CROP ? null : state.pendingOutputSize,
        activeFormatId: tool === TOOLS.CROP ? state.activeFormatId || state.cropRatio : state.activeFormatId,
      };
    }
    case 'SET_CROP_RECT':
      return {
        ...state,
        cropRect: action.payload,
      };
    case 'APPLY_DOCUMENT_CROP': {
      const { width, height, elements } = action.payload;
      return {
        ...state,
        width,
        height,
        elements,
        outputSize: state.pendingOutputSize || null,
        pendingOutputSize: null,
        tool: TOOLS.SELECT,
        cropRect: null,
        selectedId: state.selectedId,
      };
    }
    case 'SET_VIEWPORT':
      return {
        ...state,
        viewport: { ...state.viewport, ...action.payload },
      };
    case 'SET_UI':
      return {
        ...state,
        ui: { ...state.ui, ...action.payload },
      };
    case 'RESTORE_SNAPSHOT': {
      const snapshot = action.payload;
      return {
        ...state,
        width: snapshot.width,
        height: snapshot.height,
        background: snapshot.background,
        outputSize: snapshot.outputSize || null,
        elements: snapshot.elements,
        selectedId: snapshot.selectedId,
        selectedIds: snapshot.selectedIds || (snapshot.selectedId ? [snapshot.selectedId] : []),
        hasDocument: true,
        tool: TOOLS.SELECT,
        cropRect: null,
      };
    }
    case 'RESET':
      return createInitialState();
    default:
      return state;
  }
};
